import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getFaqCategoryByIdAsync, updateFaqCategoryAsync } from '@application/features/faqCategory/faqCategoryThunk';
import { getFamilyFestFaqCategoryByIdAsync, updateFamilyFestFaqCategoryAsync } from '@application/features/familyFestFaqCategory/familyFestFaqCategoryThunk';
import { FamilyFestFaqTypeEnum, faqTypeOfCategory } from '@application/enums/family-fest-faq-type-enum';
import { filterLanguagesByEventType } from '@application/helpers/language-filter-helper';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Box, Button, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useFaqTypeOptions } from '../FaqTypeFilter';
const DetailsContent = ({ category, isFamilyEvent, onFaqTypeChanged, onCategorySaved }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isSaving, setIsSaving] = useState(false);
    const faqTypeOptions = useFaqTypeOptions();
    const allLanguagesState = useAppSelector((state) => state.languages.languages);
    const eventLanguagesState = useAppSelector((state) => state.languages.eventLanguages);
    const standardSelectedCategory = useAppSelector((state) => state.faqCategory.selectedCategory);
    const familyFestSelectedCategory = useAppSelector((state) => state.familyFestFaqCategory.selectedCategory);
    const selectedCategory = isFamilyEvent ? familyFestSelectedCategory : standardSelectedCategory;
    const allLanguages = allLanguagesState ?? [];
    const eventLanguagesRaw = (eventLanguagesState ?? []).slice();
    // Filter languages based on event type - exclude SV for non-FamilyFest events
    const filteredEventLanguagesRaw = filterLanguagesByEventType(eventLanguagesRaw, isFamilyEvent);
    const eventLanguages = filteredEventLanguagesRaw
        .map((eventLang) => {
        const globalLang = allLanguages.find((item) => item.id === eventLang.id);
        return globalLang
            ? {
                ...globalLang,
                ...eventLang,
                isPrimary: eventLang.isPrimary // critical: preserve event primary
            }
            : eventLang;
    })
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
    const fallbackEn = allLanguages.find((l) => (l.code || '').toLowerCase() === 'en') ??
        {
            id: '',
            code: 'en',
            name: 'English',
            isPrimary: true
        };
    const primaryLanguage = eventLanguages.find((l) => l.isPrimary) ?? eventLanguages[0] ?? fallbackEn;
    const translationLanguages = eventLanguages.filter((l) => l.id && l.id !== primaryLanguage?.id);
    const translationLanguageIds = translationLanguages.map((l) => l.id).join('|');
    const { control, reset, handleSubmit, watch, formState: { isDirty } } = useForm({
        defaultValues: {
            name: '',
            faqType: FamilyFestFaqTypeEnum.Registration,
            translations: {}
        }
    });
    // Only FamilyFest categories carry a type; the standard category DTO doesn't have the field.
    const faqTypeOf = (activeSelectedCategory) => faqTypeOfCategory({
        familyFestFaqType: activeSelectedCategory
            ?.familyFestFaqType ?? category?.familyFestFaqType
    });
    const baseName = watch('name');
    const lastResetSignatureRef = useRef('');
    useEffect(() => {
        if (!category?.id)
            return;
        if (isFamilyEvent) {
            dispatch(getFamilyFestFaqCategoryByIdAsync({ id: category.id }));
        }
        else {
            dispatch(getFaqCategoryByIdAsync({ id: category.id }));
        }
    }, [dispatch, category?.id, isFamilyEvent]);
    // add near language setup
    const englishLanguageId = allLanguages.find((l) => (l.code || '').toLowerCase() === 'en')?.id;
    // replace the reset useEffect body
    useEffect(() => {
        const activeSelectedCategory = selectedCategory?.id === category?.id ? selectedCategory : undefined;
        const translations = activeSelectedCategory?.translations ?? [];
        const primaryTranslation = translations.find((tr) => tr.languageId === primaryLanguage?.id)?.name;
        const translationMap = {};
        for (const lang of translationLanguages) {
            const match = translations.find((tr) => tr.languageId === lang.id);
            let translationName = match?.name ?? '';
            // if EN became non-primary, preserve old base value into EN translation input
            if (!translationName && lang.id === englishLanguageId) {
                translationName = activeSelectedCategory?.name ?? category?.name ?? '';
            }
            translationMap[lang.id] = { name: translationName };
        }
        const nextValues = {
            // base field follows current primary language
            name: primaryTranslation ?? activeSelectedCategory?.name ?? category?.name ?? '',
            faqType: faqTypeOf(activeSelectedCategory),
            translations: translationMap
        };
        const signature = JSON.stringify(nextValues);
        if (signature !== lastResetSignatureRef.current) {
            reset(nextValues);
            lastResetSignatureRef.current = signature;
        }
    }, [
        selectedCategory,
        category?.id,
        category?.name,
        translationLanguageIds,
        primaryLanguage?.id,
        englishLanguageId,
        reset
    ]);
    const handleReset = () => {
        const activeSelectedCategory = selectedCategory?.id === category?.id ? selectedCategory : undefined;
        const translations = activeSelectedCategory?.translations ?? [];
        const translationMap = {};
        for (const lang of translationLanguages) {
            const match = translations.find((tr) => tr.languageId === lang.id);
            translationMap[lang.id] = { name: match?.name ?? '' };
        }
        const nextValues = {
            name: activeSelectedCategory?.name ?? category?.name ?? '',
            faqType: faqTypeOf(activeSelectedCategory),
            translations: translationMap
        };
        reset(nextValues);
        lastResetSignatureRef.current = JSON.stringify(nextValues);
    };
    const onSubmit = (data) => {
        const trimmedName = data.name.trim();
        if (!category?.id || !trimmedName)
            return;
        const translations = translationLanguages
            .map((lang) => ({
            languageId: lang.id,
            name: data.translations?.[lang.id]?.name?.trim() ?? ''
        }))
            .filter((item) => !!item.languageId);
        const request = {
            id: category.id,
            name: trimmedName,
            translations
        };
        setIsSaving(true);
        const previousFaqType = faqTypeOf(selectedCategory?.id === category.id ? selectedCategory : undefined);
        const dispatchAction = isFamilyEvent
            ? dispatch(updateFamilyFestFaqCategoryAsync({
                ...request,
                familyFestFaqType: data.faqType
            }))
            : dispatch(updateFaqCategoryAsync(request));
        dispatchAction
            .unwrap()
            .then((response) => {
            if (response.isSuccess && response.data) {
                const responseTranslations = response.data.translations ?? [];
                const nextTranslations = translationLanguages.reduce((acc, lang) => {
                    acc[lang.id] = {
                        name: responseTranslations.find((tr) => tr.languageId === lang.id)?.name ?? ''
                    };
                    return acc;
                }, {});
                const nextValues = {
                    name: response.data.name,
                    faqType: isFamilyEvent ? data.faqType : FamilyFestFaqTypeEnum.Registration,
                    translations: nextTranslations
                };
                reset(nextValues);
                lastResetSignatureRef.current = JSON.stringify(nextValues);
                if (isFamilyEvent) {
                    dispatch(getFamilyFestFaqCategoryByIdAsync({ id: category.id }));
                    // The list is loaded per type: if the category moved, switch the list to its new type
                    // (which reloads it); otherwise just reload the current type for the new name.
                    if (data.faqType !== previousFaqType)
                        onFaqTypeChanged?.(data.faqType);
                    else
                        onCategorySaved?.();
                }
                else {
                    dispatch(getFaqCategoryByIdAsync({ id: category.id }));
                }
                toast.success(response.message || t('events.eventDetails.faqTab.updateCategorySuccessfully'));
            }
            else {
                toast.error(response.message || t('events.eventDetails.faqTab.failedtoupdateCategory'));
            }
        })
            .catch(() => { })
            .finally(() => {
            setIsSaving(false);
        });
    };
    const primaryCode = (primaryLanguage?.code || 'en').toUpperCase();
    return (_jsxs(Grid, { container: true, columnSpacing: 2, children: [_jsxs(Grid, { size: { xs: 12, md: translationLanguages.length > 0 ? 6 : 12 }, children: [_jsx(Box, { pt: 2, children: _jsx(Typography, { variant: 'h4', children: t('events.eventDetails.faqTab.categoryDetailsLang', { lang: primaryCode }) }) }), _jsxs(Box, { pt: '12px', display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'name', label: t('events.eventDetails.faqTab.newCategoryDialog.categoryName') }), isFamilyEvent && (_jsx(SingleSelect, { bg: 'grey8', control: control, name: 'faqType', label: t('events.eventDetails.faqTab.faqType.categoryLabel'), options: faqTypeOptions, disableClearable: true }))] }), _jsxs(Box, { display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2, children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: handleReset, disabled: isSaving || !isDirty, children: t('actions.reset') }), _jsx(Button, { variant: 'contained', color: 'primary', size: 'large', onClick: handleSubmit(onSubmit), disabled: !category?.id || !baseName?.trim() || !isDirty || isSaving, children: t('actions.saveChanges') })] })] }), translationLanguages.length > 0 && (_jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsxs(Box, { borderRadius: 3, padding: '0px 16px 16px 16px', boxShadow: theme.customShadows.card, children: [_jsx(Box, { pt: 2, children: _jsx(Typography, { variant: 'subtitle2', children: t('events.eventDetails.faqTab.translations') }) }), _jsx(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: translationLanguages.map((lang) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', width: '100%', children: [_jsxs(Box, { py: 1, display: 'flex', gap: 1, alignItems: 'center', width: '100%', children: [_jsx(Box, { component: 'img', src: lang.flagUrl || `/assets/images/flags/${(lang.code || '').toLowerCase()}-flag.svg`, alt: (lang.code || '').toUpperCase(), width: 28, height: 'auto' }), _jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: _jsx(Typography, { variant: 'subtitle1', noWrap: true, children: (lang.code || '').toUpperCase() }) })] }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: `translations.${lang.id}.name`, label: t('events.eventDetails.faqTab.newCategoryDialog.categoryName') })] }, lang.id))) })] }) }))] }));
};
export default DetailsContent;
